import { defineStore } from 'pinia'

export type NavigationTransitionState =
  | 'idle'
  | 'programmatic-scroll'
  | 'restoring-from-modal'

type ScrollLockReason = 'modal' | 'transition' | 'typing' | 'submitting'

type ScrollLocks = Record<ScrollLockReason, boolean>

const createScrollLocks = (): ScrollLocks => ({
  modal: false,
  transition: false,
  typing: false,
  submitting: false,
})

const resolveValidSectionId = (
  sections: string[],
  ...candidates: Array<string | null | undefined>
): string | null => {
  for (const candidate of candidates) {
    if (candidate && sections.includes(candidate)) {
      return candidate
    }
  }

  return sections[0] ?? null
}

const resolveOptionalSectionId = (
  sections: string[],
  ...candidates: Array<string | null | undefined>
): string | null => {
  for (const candidate of candidates) {
    if (candidate && sections.includes(candidate)) {
      return candidate
    }
  }

  return null
}

export const useFormNavigationStore = defineStore('formNavigation', {
  state: () => ({
    sections: [] as string[],
    activeSectionId: null as string | null,
    visibleSectionId: null as string | null,
    lastStableSectionId: null as string | null,
    pendingTargetSectionId: null as string | null,
    modalOriginSectionId: null as string | null,
    transitionState: 'idle' as NavigationTransitionState,
    scrollLocks: createScrollLocks(),
  }),
  getters: {
    currentSectionIndex(state): number {
      if (state.sections.length === 0) {
        return 0
      }

      const index = state.activeSectionId ? state.sections.indexOf(state.activeSectionId) : -1
      return index >= 0 ? index : 0
    },
    visibleSectionIndex(state): number {
      if (state.sections.length === 0) {
        return 0
      }

      const index = state.visibleSectionId ? state.sections.indexOf(state.visibleSectionId) : -1
      return index >= 0 ? index : 0
    },
    isFirstSection(): boolean {
      return this.currentSectionIndex <= 0
    },
    isLastSection(): boolean {
      return this.sections.length > 0 && this.currentSectionIndex >= this.sections.length - 1
    },
    nextSectionId(): string | null {
      if (this.isLastSection) {
        return null
      }

      return this.sections[this.currentSectionIndex + 1] ?? null
    },
    previousSectionId(): string | null {
      if (this.isFirstSection) {
        return null
      }

      return this.sections[this.currentSectionIndex - 1] ?? null
    },
    isTransitioning(state): boolean {
      return state.transitionState !== 'idle'
    },
    isScrollLocked(state): boolean {
      return Object.values(state.scrollLocks).some(Boolean)
    },
    canTrackViewport(state): boolean {
      return !state.scrollLocks.typing && !state.scrollLocks.modal && !state.scrollLocks.submitting
    },
  },
  actions: {
    setSections(sectionIds: string[]) {
      const nextSections = [...sectionIds]
      this.sections = nextSections

      // If the current active section was removed from the list, fall back
      // to the nearest valid section without jumping ahead.
      if (this.activeSectionId && !nextSections.includes(this.activeSectionId)) {
        // Fall back to the section just before where the old one sat, or the first section
        const currentIndex = this.currentSectionIndex
        const fallback = nextSections[Math.min(currentIndex, nextSections.length - 1)] ?? nextSections[0] ?? null
        this.activeSectionId = fallback
        this.visibleSectionId = fallback
        this.lastStableSectionId = fallback
      }

      this.pendingTargetSectionId = resolveOptionalSectionId(nextSections, this.pendingTargetSectionId)
      this.modalOriginSectionId = resolveOptionalSectionId(nextSections, this.modalOriginSectionId)
    },
    setSettledSection(sectionId: string | null) {
      const resolvedSectionId = resolveValidSectionId(this.sections, sectionId)

      this.activeSectionId = resolvedSectionId
      this.visibleSectionId = resolvedSectionId
      this.lastStableSectionId = resolvedSectionId
      this.pendingTargetSectionId = null
      this.transitionState = 'idle'
      this.scrollLocks.transition = false
    },
    beginSectionTransition(
      sectionId: string,
      transitionState: Exclude<NavigationTransitionState, 'idle'> = 'programmatic-scroll',
    ): boolean {
      if (!this.sections.includes(sectionId)) {
        return false
      }

      if (this.scrollLocks.modal || this.scrollLocks.submitting) {
        return false
      }

      // If a previous transition is stuck, force-complete it rather than
      // silently rejecting the new navigation. This prevents the footer
      // from disappearing when transitions fail to settle.
      if (this.transitionState !== 'idle' && this.pendingTargetSectionId !== sectionId) {
        this.completeSectionTransition(this.pendingTargetSectionId)
      }

      this.activeSectionId = sectionId
      this.pendingTargetSectionId = sectionId
      this.transitionState = transitionState
      this.scrollLocks.transition = true

      return true
    },
    completeSectionTransition(sectionId?: string | null) {
      const resolvedSectionId = resolveValidSectionId(
        this.sections,
        sectionId,
        this.pendingTargetSectionId,
        this.activeSectionId,
        this.lastStableSectionId,
      )
      const restoringFromModal = this.transitionState === 'restoring-from-modal'

      this.activeSectionId = resolvedSectionId
      this.visibleSectionId = resolvedSectionId
      this.lastStableSectionId = resolvedSectionId
      this.pendingTargetSectionId = null
      this.transitionState = 'idle'
      this.scrollLocks.transition = false

      if (restoringFromModal) {
        this.modalOriginSectionId = null
      }
    },
    reportVisibleSection(sectionId: string) {
      if (!this.sections.includes(sectionId)) {
        return
      }

      this.visibleSectionId = sectionId

      if (this.pendingTargetSectionId === sectionId) {
        this.completeSectionTransition(sectionId)
      }
    },
    rememberModalOrigin(sectionId?: string | null) {
      this.modalOriginSectionId = resolveValidSectionId(
        this.sections,
        sectionId,
        this.activeSectionId,
        this.lastStableSectionId,
      )
      this.scrollLocks.modal = true
    },
    prepareModalRestore(): string | null {
      this.scrollLocks.modal = false

      const targetSectionId = resolveValidSectionId(
        this.sections,
        this.modalOriginSectionId,
        this.activeSectionId,
        this.lastStableSectionId,
      )

      if (!targetSectionId) {
        this.modalOriginSectionId = null
        return null
      }

      this.activeSectionId = targetSectionId
      this.pendingTargetSectionId = targetSectionId
      this.transitionState = 'restoring-from-modal'
      this.scrollLocks.transition = true

      return targetSectionId
    },
    clearModalState() {
      this.modalOriginSectionId = null
      this.scrollLocks.modal = false
    },
    setScrollLock(reason: ScrollLockReason, isLocked: boolean) {
      this.scrollLocks[reason] = isLocked
    },
  },
})
